/* globals gon */

import $ from 'jquery'
import store from 'store'
import User from '../models/user'
import Cookies from 'js-cookie'
import Util from '../utils'
import LocaleService from './locale_service'

const CACHE_TIME = 180000 // 3 minutes

export default class UserService {
  static getCurrent() {
    return new UserService().getCurrent()
  }

  getCurrent() {
    const user = this._getUserFromCache()
    if (user != null) {
      return $.when(user)
    } else {
      return this._getLoginDetails().then(
        user => {
          if (user != null) {
            this._storeUserInCache(user)
          }
          return user
        },
        err => {
          console.error(err)
          return $.Deferred().resolve(null)
        }
      )
    }
  }

  login(username, password, recaptchaResponse) {
    return this._makeLoginRequest(username, password, recaptchaResponse)
  }

  getUserIdFromCache() {
    const attribs = store.get('user')
    if (attribs) {
      return attribs.user_id
    }

    return null
  }

  _getUserFromCache() {
    if (this._isCacheFresh()) {
      const attribs = store.get('user')
      if (attribs != null) {
        return new User(attribs)
      }
    }

    return null
  }

  _storeUserInCache(user) {
    store.set('userLastUpdated', new Date().getTime())
    return store.set('user', user.toJSON())
  }

  _isCacheFresh() {
    const lastUpdated = store.get('userLastUpdated')
    if (lastUpdated != null) {
      const now = new Date().getTime()
      return now - lastUpdated < CACHE_TIME
    } else {
      return false
    }
  }

  _getLoginDetails() {
    return $.get('/login/get')
      .then(response => response.reply)
      .then(response => this._addUserDetails(response))
      .then(response => this._createUserFromResponse(response))
  }

  _addUserDetails(loginStatus) {
    if (!loginStatus || loginStatus.login !== true) {
      return loginStatus
    }
    return $.get('/api/user/get')
      .then(response => Object.assign({}, loginStatus, response))
      .fail(err => `Failed getting user details. status: ${err.status}`)
  }

  _makeLoginRequest(username, password, recaptchaResponse) {
    const data = {
      user_id: username, // eslint-disable-line camelcase
      password,
      recaptchaResponse,
      recaptchaPublic: gon.config.recaptcha.login_key
    }

    return $.post('/login/create', data)
      .then(
        response => response.reply,
        () => 'Server error'
      )
      .then(response => this._addUserDetails(response))
      .then(response => {
        const user = this._createUserFromResponse(response)
        if (user) {
          store.set('user', user.toJSON())
          return this._createLoginResponse(response)
        } else {
          return $.Deferred().reject(this._createLoginResponse(response))
        }
      })
  }

  _createLoginResponse(response) {
    const code = response && response.error != null ? response.error : -1
    const message = response && response.message ? response.message : 'An unknown error has occured'
    return { code, message }
  }

  _createUserFromResponse(response) {
    if (response != null && response.login === true) {
      return new User(response)
    } else {
      return null
    }
  }

  logout(options) {
    if (options == null) {
      options = {}
    }
    return $.post('/login/destroy').then(() => {
      this.clearSession()

      if (this.requiresLogin()) {
        this.redirectToLogin({ withQuery: options.redirectPathWithQuery })
      }
    })
  }

  clearSession() {
    Cookies.remove('_web_session', { path: '/' })
    return store.remove('user')
  }

  redirectToLogin(options) {
    if (options == null) {
      options = {}
    }
    let currentPath = ''

    if (options.withQuery) {
      currentPath = encodeURIComponent(Util.getCurrentPathWithQuery())
    } else {
      currentPath = Util.getCurrentPath()
    }

    const loginPath = LocaleService.localizePath(`/signin?redirect=${currentPath}`)
    Util.redirect(loginPath)
  }

  requiresLogin() {
    return gon.requiresLogin === true
  }

  ensureLoggedIn(options) {
    if (options == null) {
      options = {}
    }
    const $dfd = $.Deferred()
    $.when(this.getCurrent())
      .done(user => {
        if (user != null) {
          return $dfd.resolve(user)
        } else {
          $dfd.reject()
          return this.redirectToLogin({ withQuery: options.redirectPathWithQuery })
        }
      })
      .fail(() => {
        $dfd.reject()
        return this.redirectToLogin({ withQuery: options.redirectPathWithQuery })
      })

    return $dfd
  }
}
